// Step changes on the steps tables (docs/step-changes-brief.md, 2026-10-24; the drawing's section 5, rows PS1 to PS6). An AI
// employee's change to the process is one record; here it shows in place: a ghost row for an add, the proposed value under the
// current one for a change, "Retire → replacement" beside a name, a stage setting beside the current one in the stage header, and
// a fixed "Suggested" column at the far right with the kind word, the reason on one line, Approve naming what it writes, Change
// (the proposed cells become editable, with a note box: edit a value and the button reads "Approve as edited"; write a note and
// it reads "Send back to Alex") and Send back. A stale suggestion (its row changed since) dims and leaves only Send back. A row an
// AI employee changed directly shows who, when and Undo, with the old value small under the new one. On the phone the pending
// changes are cards above the stages with the same three buttons.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { approveWords, changedColumns, KIND_WORD, oneLine, standingChanges, stepChangeStale, stepChangeWords, stepColumnLabel, stepValueWords, type StepChangeContext, type StepChangeRow } from '@zerogap/engine';
import { toast, type Row } from '../../lib/api';
import type { Pipeline } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { changeContext, changeView, decideStepChange, rowNow, undoStepChange, useChangeFromAddress, useStepChanges } from '../../lib/stepchanges';
import { stamp } from './StepTable';

export interface SuggestState {
  loading: boolean;
  view: 'pipeline' | 'workflow';
  ctx: StepChangeContext;
  /** Every change on this tab's rows. */
  all: StepChangeRow[];
  /** The suggestions waiting on this tab. */
  waiting: StepChangeRow[];
  /** The newest applied or approved change still standing on each row (who, when, Undo). */
  direct: Map<string, StepChangeRow>;
  nowOf: (c: StepChangeRow) => Record<string, unknown> | null;
  stale: (c: StepChangeRow) => boolean;
  who: (id: unknown) => string;
  /** The waiting adds that go in a stage (pipeline) or among the job steps (workflow). */
  ghostsIn: (stageId: string | null) => StepChangeRow[];
  /** The waiting change of one kind on a row. */
  on: (targetId: string, kind: StepChangeRow['kind']) => StepChangeRow | undefined;
  /** The value a change proposes for a column, as edited so far when Spencer is changing it. */
  proposed: (c: StepChangeRow, col: string) => unknown;
  /** Whether a change carries a proposal for the column. */
  proposes: (c: StepChangeRow, col: string) => boolean;
  editing: (c: StepChangeRow) => boolean;
  toggleEditing: (c: StepChangeRow) => void;
  setEdit: (c: StepChangeRow, col: string, value: unknown) => void;
  edited: (c: StepChangeRow) => boolean;
  note: (c: StepChangeRow) => string;
  setNote: (c: StepChangeRow, text: string) => void;
  approve: (c: StepChangeRow) => Promise<void>;
  sendBack: (c: StepChangeRow) => Promise<void>;
  undo: (c: StepChangeRow) => Promise<void>;
  /** The change the address named, lit for two seconds. */
  lit: string | null;
  canDecide: boolean;
}

/** The state one steps tab keeps about the changes on it. */
export function useSuggest(view: 'pipeline' | 'workflow', p: Pipeline, wsteps: Row[], openFor: (c: StepChangeRow) => void): SuggestState {
  const s = useSession();
  const changes = useStepChanges();
  const [editing, setEditing] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [lit, setLit] = useState<string | null>(null);
  const ctx = useMemo(() => changeContext(p, wsteps), [p, wsteps]);
  const all = useMemo(() => changes.rows.filter((c) => changeView(c) === view), [changes.rows, view]);
  const waiting = useMemo(() => all.filter((c) => c.status === 'waiting'), [all]);
  const nowOf = useCallback((c: StepChangeRow) => rowNow(c, p, wsteps), [p, wsteps]);
  const direct = useMemo(() => standingChanges(all, nowOf), [all, nowOf]);
  const sameName = (c: StepChangeRow) => {
    const name = String(c.proposed.name ?? '').toLowerCase();
    return c.target_table === 'pipeline_step' ? p.steps.some((x) => !x.retired_at && x.stage_id === c.stage_id && x.name.toLowerCase() === name) : wsteps.some((x) => !x.retired_at && String(x.name).toLowerCase() === name);
  };
  const open = useCallback((c: StepChangeRow) => openFor(c), [openFor]);
  const light = useCallback((id: string | null) => setLit(id), []);
  useChangeFromAddress(changes.loading ? [] : all, open, light);
  const who = (id: unknown) => (p.users.find((u) => u.id === id)?.name as string | undefined) ?? 'an AI employee';
  const clear = (c: StepChangeRow) => {
    setEditing((e) => {
      const n = new Set(e);
      n.delete(c.id);
      return n;
    });
    setEdits((e) => {
      const n = { ...e };
      delete n[c.id];
      return n;
    });
    setNotes((e) => {
      const n = { ...e };
      delete n[c.id];
      return n;
    });
  };
  const say = (e: unknown) => toast(e instanceof Error ? e.message.replace(/^[a-z_]+ refused \(\d+\): /, '') : String(e));
  return {
    loading: changes.loading,
    view,
    ctx,
    all,
    waiting,
    direct,
    nowOf,
    stale: (c) => stepChangeStale(c, nowOf(c), sameName(c)),
    who,
    ghostsIn: (stageId) => waiting.filter((c) => c.kind === 'add' && (view === 'workflow' || c.stage_id === stageId)).sort((a, b) => Number(a.proposed.position ?? 0) - Number(b.proposed.position ?? 0)),
    on: (targetId, kind) => waiting.find((c) => c.target_id === targetId && c.kind === kind),
    proposed: (c, col) => (edits[c.id] && col in edits[c.id]! ? edits[c.id]![col] : c.proposed[col]),
    proposes: (c, col) => col in c.proposed && !(c.kind === 'change' && !changedColumns(c).includes(col) && col !== 'event_key'),
    editing: (c) => editing.has(c.id),
    toggleEditing: (c) => {
      if (editing.has(c.id)) clear(c);
      else setEditing((e) => new Set(e).add(c.id));
    },
    setEdit: (c, col, value) => setEdits((e) => ({ ...e, [c.id]: { ...(e[c.id] ?? {}), [col]: value } })),
    edited: (c) => !!edits[c.id] && Object.keys(edits[c.id]!).some((k) => JSON.stringify(edits[c.id]![k] ?? null) !== JSON.stringify(c.proposed[k] ?? null)),
    note: (c) => notes[c.id] ?? '',
    setNote: (c, text) => setNotes((e) => ({ ...e, [c.id]: text })),
    approve: async (c) => {
      try {
        await decideStepChange(c.id, true, edits[c.id] && Object.keys(edits[c.id]!).length ? edits[c.id]! : null, notes[c.id]?.trim() || null);
        clear(c);
      } catch (e) {
        say(e);
      }
    },
    sendBack: async (c) => {
      try {
        await decideStepChange(c.id, false, null, notes[c.id]?.trim() || null);
        clear(c);
      } catch (e) {
        say(e);
      }
    },
    undo: async (c) => {
      try {
        await undoStepChange(c.id);
      } catch (e) {
        say(e);
      }
    },
    lit,
    canDecide: s.canWrite && (s.canEdit('screen.settings') || s.canEdit('change.pipeline_process')),
  };
}

/** The proposed value under the current one, in the suggestion accent ("→ Pipeline manager"). */
export function Prop({ children, dim }: { children: ReactNode; dim?: boolean }) {
  return <small className={`sprop${dim ? ' sstale' : ''}`}>→ {children}</small>;
}

/** The old value small under the new one, after a direct write ("was: Only if a revision is asked for"). */
export function Was({ children }: { children: ReactNode }) {
  return <small className="swas">was: {children}</small>;
}

/** Who changed the row directly, when, and Undo (row PS3: nothing else). */
export function DirectNote({ s, c, undoWords }: { s: SuggestState; c: StepChangeRow; undoWords?: string }) {
  return (
    <span className="sdirect" title={`${KIND_WORD[c.kind]}: ${stepChangeWords(c, s.ctx)}${c.reason ? ` · ${c.reason}` : ''}`}>
      <span>
        {s.who(c.suggested_by)} · {stamp(c.decided_at ?? c.suggested_at)}
      </span>
      {s.canDecide && (
        <button type="button" className="linklike" onClick={() => void s.undo(c)} title={undoWords ?? 'Put back what this change replaced'}>
          Undo
        </button>
      )}
    </span>
  );
}

/** The Suggested column's cell for one waiting change: kind, who and why, and the three buttons (or, stale, only Send back). */
export function SuggestCell({ s, c, buttons = true, stepChanged }: { s: SuggestState; c: StepChangeRow; buttons?: boolean; /** Who changed the row since it was filed, and when: the stale words. */ stepChanged?: { who: string; at: string } | null }) {
  const [full, setFull] = useState(false);
  const stale = s.stale(c);
  const editing = s.editing(c);
  const edited = s.edited(c);
  const note = s.note(c).trim();
  const reason = c.reason ?? '';
  return (
    <div className={`ssg${stale ? ' stale' : ''}`} data-change={c.id}>
      <div className="ssg-head">
        <b>{KIND_WORD[c.kind]}</b>
        <span className="ssg-who">
          {s.who(c.suggested_by)} · {stamp(c.suggested_at)}
        </span>
      </div>
      {reason && (
        <button type="button" className={`ssg-why${full ? ' full' : ''}`} onClick={() => setFull(!full)} title={full ? 'Fold the reason' : reason}>
          “{full ? reason : oneLine(reason)}”
        </button>
      )}
      {stale ? (
        <div className="ssg-stale">Step changed{stepChanged ? ` ${stamp(stepChanged.at)} by ${stepChanged.who}` : ' since this was filed'}</div>
      ) : null}
      {editing && !stale && (
        <input className="sin ssg-note" value={s.note(c)} placeholder={`A note to ${s.who(c.suggested_by)} (optional)`} onChange={(e) => s.setNote(c, e.target.value)} aria-label="A note on the decision" />
      )}
      {buttons && s.canDecide && (
        <div className="ssg-act">
          {!stale && (
            <button type="button" className="sob p" onClick={() => void s.approve(c)}>
              {edited ? 'Approve as edited' : approveWords(c, s.ctx)}
            </button>
          )}
          {!stale && (
            <button type="button" className={`sob${editing ? ' on' : ''}`} onClick={() => s.toggleEditing(c)}>
              {editing ? 'Stop changing' : 'Change'}
            </button>
          )}
          <button type="button" className="sob" onClick={() => void s.sendBack(c)}>
            {note || stale ? `Send back to ${s.who(c.suggested_by)}` : 'Send back'}
          </button>
        </div>
      )}
    </div>
  );
}

/** "2 suggested" on a stage header, in the suggestion accent. */
export function SuggestChip({ n, words = 'suggested' }: { n: number; words?: string }) {
  if (!n) return null;
  return (
    <span className="schip">
      {n} {words}
    </span>
  );
}

/** A stage setting proposed: the current value with the proposal beside it, and the buttons, on the stage header's right. */
export function StageSuggest({ s, c, current }: { s: SuggestState; c: StepChangeRow; current: Record<string, unknown> }) {
  const editing = s.editing(c);
  return (
    <span className="sstg-sug" data-change={c.id} onClick={(e) => e.stopPropagation()}>
      {changedColumns(c).map((k) => (
        <span key={k} className="sstg-kv">
          <span className="k">{stepColumnLabel('pipeline_stage', k)}</span>
          <span>{stepValueWords('pipeline_stage', k, current[k], s.ctx)}</span>
          {editing && !s.stale(c) ? (
            <input
              className="sin ssg-val"
              value={String(s.proposed(c, k) ?? '')}
              aria-label={`Proposed ${stepColumnLabel('pipeline_stage', k)}`}
              onChange={(e) => s.setEdit(c, k, k === 'note' ? e.target.value : e.target.value === '' ? null : Number(e.target.value))}
            />
          ) : (
            <Prop dim={s.stale(c)}>{stepValueWords('pipeline_stage', k, s.proposed(c, k), s.ctx)}</Prop>
          )}
        </span>
      ))}
      <SuggestCell s={s} c={c} />
    </span>
  );
}

/** What Approve writes, restated field by field, for the phone card. */
export interface CardLine {
  k: string;
  v: ReactNode;
}

/** The phone: one card per pending change with the same three buttons, then the direct changes with Undo (row PS4). */
export function PendingCards({ s, lines, directLines }: { s: SuggestState; lines: (c: StepChangeRow) => CardLine[]; directLines: (c: StepChangeRow) => CardLine[] }) {
  const direct = [...s.direct.values()].sort((a, b) => String(b.decided_at ?? b.suggested_at).localeCompare(String(a.decided_at ?? a.suggested_at)));
  if (!s.waiting.length && !direct.length) return null;
  return (
    <div className="spend">
      {s.waiting.map((c) => (
        <div key={c.id} className={`spcard${s.stale(c) ? ' stale' : ''}`} data-change={c.id}>
          <h5>
            {c.target_table === 'pipeline_stage' ? s.ctx.stageName(c.target_id) : c.stage_id ? s.ctx.stageName(c.stage_id) : 'Job steps'} · {KIND_WORD[c.kind].toLowerCase()}
            {c.kind === 'add' ? ' a step' : ''}
          </h5>
          <div className="kv">
            {lines(c).map((l, i) => (
              <span key={i} className="kvrow">
                <span>{l.k}</span>
                <span>{l.v}</span>
              </span>
            ))}
            <span className="kvrow">
              <span>Why</span>
              <span>{c.reason ? `“${c.reason}”` : '—'}</span>
            </span>
            <span className="kvrow">
              <span>From</span>
              <span>
                {s.who(c.suggested_by)} · {stamp(c.suggested_at)}
              </span>
            </span>
          </div>
          <SuggestCell s={s} c={c} />
        </div>
      ))}
      {direct.length > 0 && (
        <>
          <div className="lbl">Changed directly</div>
          {direct.map((c) => (
            <div key={c.id} className="spcard plain">
              <div className="kv">
                {directLines(c).map((l, i) => (
                  <span key={i} className="kvrow">
                    <span>{l.k}</span>
                    <span>{l.v}</span>
                  </span>
                ))}
                <span className="kvrow">
                  <span>By</span>
                  <span>
                    {s.who(c.suggested_by)} · {stamp(c.decided_at ?? c.suggested_at)}
                  </span>
                </span>
              </div>
              {s.canDecide && (
                <div className="ssg-act">
                  <button type="button" className="sob" onClick={() => void s.undo(c)}>
                    Undo
                  </button>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** The level a proposed step would read (ug.step_level, mirrored): done by an event L0; the Owner seat L3; the client, no level; the Owner approves L1; else L2. */
export function ghostLevel(p: Pipeline, seatId: unknown, doneRule: unknown): string | null {
  if (doneRule === 'event') return 'L0';
  const seat = p.seats.find((x) => x.id === seatId);
  if (seat?.key === 'owner') return 'L3';
  if (!seat || seat.key === 'client' || doneRule === 'client_acts') return null;
  return doneRule === 'owner_approves' ? 'L1' : 'L2';
}
